import * as React from 'react';
import { Formik, FormikProps } from 'formik';
import AsyncActionContainer, { AsyncActionContainerState } from '../../../commonComponents/AsyncActionContainer';
import Loading from '../../../commonComponents/Loading';
import {
	getInitialValuesForSections,
	validateFormSpecificationPageSections,
	getInvisibleFields
} from '../../common/dataCapture/form';
import { Section } from '../../../models/be/db/FormSpecification';
import DataCaptureSection from '../../common/dataCapture/DataCaptureSection';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import SecondaryButton from '../../common/buttons/SecondaryButton';
import { evalBoolean } from '../../../core/ast';
import Reference from '../../../models/be/Reference';
import { hasValue, setNestedFieldValue } from '../../../lib/utils';
import MessageBox from '../../common/MessageBox';
import './ReferenceForm.less';
import { referenceTypes } from '../../../core/domain';
import Buttons from '../../common/Buttons';

export default class ReferenceForm extends AsyncActionContainer<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			...this.state
		};
	}

	handleSaveButtonClick = async (formikProps: FormikProps<any>) => {
		await this.performAction(async () => {
			// formik validateOnMount has been set to true, so formik.isValid is up to date.
			if (formikProps.isValid && !formikProps.dirty) {
				this.props.onBackButtonClick();
			} else {
				// We must submit the form when it isn't valid to let formik to set all fields to touched to show errors,
				// even though the submit handler does nothing.
				try {
					await formikProps.submitForm();
				} catch (e) {
					this.setState({ errorMessage: e.message });
					return;
				}
				if (formikProps.isValid) {
					const reference = getUpdatedReference(this.props.reference, formikProps.values);
					reference.refereeAnswersRequired = this.props.useTemplates;
					// When updating the workflow, we want to set all of the invisible fields to null, except for bool fields.
					const fieldNames = getInvisibleFields(this.props.formSections, reference);
					fieldNames.forEach((nestedFieldName) => {
						if (nestedFieldName !== 'capturedReferenceDetails.qualificationAchieved') {
							setNestedFieldValue(reference, nestedFieldName, null);
						}
					});
					// Must add await to catch any error from the async props.onSaveButtonClick.
					await this.props.onSaveButtonClick(reference);
				}
			}
		});
	};

	handleCloseErrorMessage = () => {
		this.setState({ errorMessage: null });
	};

	handleSubmit = async (values: any) => {
		const { fromDate, toDate } = values.capturedReferenceDetails;

		if (hasValue(fromDate) && hasValue(toDate) && fromDate > toDate) {
			const errorMessage = 'To date must not be before From date.';
			throw new Error(errorMessage);
		}
	};

	render() {
		const { formSections, workflowId, candidateId } = this.props;
		const initialValues = getInitialValuesForSections<any>(formSections, this.props.reference);
		// TODO: modify getInitialValuesForSections to include all observed fields.
		initialValues.userRole = this.props.userRole;
		// The following built in fields are only for candidate timeline page.
		initialValues.personalReferenceRequired = this.props.personalReferenceRequired ?? false;
		initialValues.uploadEvidenceAllowed = this.props.uploadEvidenceAllowed ?? false;
		initialValues.personalReferenceTemplateId = this.props.personalReferenceTemplateId ?? '';
		initialValues.useReferenceTemplateForReferee = this.props.useReferenceTemplateForReferee ?? false;
		initialValues.status = this.props.reference?.status ?? '';

		// TODO: Is there a way to share the common code with DataCapturePage.
		return (
			<div className="referenceForm">
				<MessageBox warning={this.state.errorMessage} onClose={this.handleCloseErrorMessage} />
				<Formik
					initialValues={initialValues}
					validate={validateFormSpecificationPageSections(formSections)}
					onSubmit={this.handleSubmit}
					validateOnMount
				>
					{(formikProps) => {
						if (this.state.loading) {
							return <Loading />;
						}
						return (
							<>
								<form
									className="k-form formLayout"
									onSubmit={(e) => {
										e.preventDefault();
									}}
								>
									{formSections
										.filter((s) => evalBoolean(s.visible, formikProps.values))
										.map((s) => (
											<DataCaptureSection
												key={s.id}
												section={s}
												values={formikProps.values}
												candidateId={candidateId}
												workflowId={workflowId}
												customerCountryCode={this.props.customerCountryCode}
											/>
										))}
								</form>
								<Buttons>
									<SecondaryButton text="Cancel" onClick={this.props.onBackButtonClick} />
									<PrimaryButton
										onClick={() => this.handleSaveButtonClick(formikProps)}
										text="Save"
										disabled={this.state.saveButtonDisabled}
									/>
								</Buttons>
							</>
						);
					}}
				</Formik>
			</div>
		);
	}
}

interface Props {
	reference: Reference;
	formSections: Section[];
	onBackButtonClick: () => void;
	onSaveButtonClick: (reference: Reference) => Promise<void>;
	personalReferenceRequired?: boolean;
	uploadEvidenceAllowed?: boolean;
	userRole: string;
	personalReferenceTemplateId?: string;
	useReferenceTemplateForReferee?: boolean;
	workflowId?: string;
	candidateId?: string;
	useTemplates?: boolean;
	customerCountryCode?: string;
}

interface State extends AsyncActionContainerState {
	initialValues: any;
	section: Section;
	saveButtonDisabled: boolean;
}

const getUpdatedReference = (reference: Reference, values: Reference) => {
	const updatedReference = hasValue(reference) ? { ...reference, ...values } : values;
	const workflowServiceName = referenceTypes.find((x) => x.value === updatedReference.workflowTypeCode).text;
	return { ...updatedReference, workflowServiceName };
};
